// Enhanced Methodology Panel with detailed calculations
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SyntheticProfileConfig } from '@/types/epse';
import { EnergyBalanceSummary } from '@/lib/epseEnergyBalance';
import { ChevronDown, Calculator, AlertTriangle, Lightbulb, Battery, Zap } from 'lucide-react';
import { useState } from 'react';

interface EPSEMethodologyPanelEnhancedProps {
  config: SyntheticProfileConfig;
  summary: EnergyBalanceSummary | null;
}

export function EPSEMethodologyPanelEnhanced({ config, summary }: EPSEMethodologyPanelEnhancedProps) {
  const [isOpen, setIsOpen] = useState(true);
  
  // Calculations
  const dailyMaxCapacity = config.connection_kw_limit * 24;
  const avgDailyDemand = config.annual_kwh / 365;
  const winterDailyDemand = (config.annual_kwh * config.season_distribution.winter) / 91;
  const peakKw = config.use_manual_peak ? config.peak_kw_manual : Math.min((avgDailyDemand / 24) / 0.6, config.connection_kw_limit * 0.95);
  const excessPeakKw = Math.max(0, peakKw - config.connection_kw_limit);
  
  const dailyDeficit = Math.max(0, winterDailyDemand - dailyMaxCapacity);
  const peakDeficitHours = excessPeakKw > 0 ? summary?.hours_above_limit || 0 : 0;
  
  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Calculator className="h-5 w-5" />
                Berekeningslogica
              </CardTitle>
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Step 1: Grid Capacity */}
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <h4 className="font-medium">1. Maximale dagcapaciteit uit net</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Hoeveel energie kan maximaal uit de netaansluiting gehaald worden?
              </p>
              <div className="font-mono text-sm bg-background p-2 rounded">
                {config.connection_kw_limit} kW × 24 uur = <span className="font-bold text-primary">{dailyMaxCapacity.toLocaleString('nl-NL')} kWh/dag</span>
              </div>
            </div>
            
            {/* Step 2: Daily Demand */}
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-primary" />
                <h4 className="font-medium">2. Geschat dagverbruik (winter werkdag)</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Hoogste verbruik is typisch in winter werkdagen
              </p>
              <div className="font-mono text-sm bg-background p-2 rounded space-y-1">
                <div>{config.annual_kwh.toLocaleString('nl-NL')} kWh × {(config.season_distribution.winter * 100).toFixed(0)}% winter / 91 dagen</div>
                <div>= <span className="font-bold text-primary">{winterDailyDemand.toLocaleString('nl-NL', { maximumFractionDigits: 0 })} kWh/dag</span></div>
              </div>
              
              {dailyDeficit > 0 && (
                <div className="flex items-center gap-2 mt-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Overschrijdt netcapaciteit met {dailyDeficit.toLocaleString('nl-NL', { maximumFractionDigits: 0 })} kWh/dag
                  </span>
                </div>
              )}
            </div>
            
            {/* Step 3: Peak Power */}
            <div className="space-y-2 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <h4 className="font-medium">3. Piekvermogen {config.use_manual_peak ? '(handmatig)' : '(berekend)'}</h4>
              </div>
              {config.use_manual_peak ? (
                <div className="font-mono text-sm bg-background p-2 rounded">
                  Handmatig ingesteld: <span className="font-bold text-primary">{config.peak_kw_manual} kW</span>
                </div>
              ) : (
                <div className="font-mono text-sm bg-background p-2 rounded space-y-1">
                  <div>Gemiddeld kW / Load Factor (0.6) = {(avgDailyDemand / 24).toFixed(1)} / 0.6</div>
                  <div>= <span className="font-bold text-primary">{peakKw.toFixed(0)} kW</span></div>
                </div>
              )}
              
              {excessPeakKw > 0 && (
                <div className="flex items-center gap-2 mt-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {excessPeakKw.toFixed(0)} kW boven contractgrens ({config.connection_kw_limit} kW)
                  </span>
                </div>
              )}
            </div>
            
            {/* Step 4: PV Contribution */}
            {config.pv_capacity_kwp > 0 && config.pv_profile_type !== 'none' && (
              <div className="space-y-2 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-green-600" />
                  <h4 className="font-medium text-green-700">4. PV bijdrage</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Zonnestroomopbrengst vermindert netafhankelijkheid
                </p>
                <div className="font-mono text-sm bg-background p-2 rounded">
                  {config.pv_capacity_kwp} kWp × ~4 piekzonuren/dag (gemiddeld) = ~{(config.pv_capacity_kwp * 4).toFixed(0)} kWh/dag
                </div>
                {summary && (
                  <Badge variant="outline" className="bg-green-500/10 text-green-700">
                    Actueel: {summary.pv_used_kwh.toFixed(0)} kWh/dag gebruikt
                  </Badge>
                )}
              </div>
            )}
            
            {/* Step 5: Battery Recommendation */}
            <div className="space-y-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2">
                <Battery className="h-4 w-4 text-primary" />
                <h4 className="font-medium">5. Batterijbehoefte voor piekvermijding</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Hoeveel BESS capaciteit is nodig om pieken onder de limiet te houden?
              </p>
              {summary && summary.shortfall_kwh > 0 ? (
                <div className="space-y-2">
                  <div className="font-mono text-sm bg-background p-2 rounded">
                    Dagelijks tekort: {summary.shortfall_kwh.toFixed(0)} kWh × 1.2 (reserve) = <span className="font-bold text-primary">{(summary.shortfall_kwh * 1.2).toFixed(0)} kWh</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="default">
                      Aanbevolen BESS: {Math.ceil(summary.shortfall_kwh * 1.2 / 50) * 50} kWh
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      (afgerond naar 50 kWh eenheden)
                    </span>
                  </div>
                </div>
              ) : (
                <Badge variant="outline" className="bg-green-500/10 text-green-700">
                  Geen tekort - BESS niet strikt noodzakelijk voor piekshaving
                </Badge>
              )}
            </div>
            
            {/* Summary if available */}
            {summary && (
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3">Samenvatting dagbalans</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="p-2 bg-muted/30 rounded">
                    <p className="text-muted-foreground">Vraag</p>
                    <p className="font-bold">{summary.total_demand_kwh.toFixed(0)} kWh</p>
                  </div>
                  <div className="p-2 bg-muted/30 rounded">
                    <p className="text-muted-foreground">Uit Net</p>
                    <p className="font-bold">{summary.actual_grid_kwh.toFixed(0)} kWh</p>
                  </div>
                  <div className="p-2 bg-muted/30 rounded">
                    <p className="text-muted-foreground">Uit Batterij</p>
                    <p className="font-bold">{summary.battery_supplied_kwh.toFixed(0)} kWh</p>
                  </div>
                  <div className="p-2 bg-muted/30 rounded">
                    <p className="text-muted-foreground">Onoplosbaar</p>
                    <p className={`font-bold ${summary.unmet_demand_kwh > 0 ? 'text-destructive' : 'text-green-600'}`}>
                      {summary.unmet_demand_kwh.toFixed(0)} kWh
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
